// context/CartContext.tsx
import { CartNotFoundError } from '@/data';
import { Cart, CartItem, CartTotals } from '@/data/interfaces/Cart';
import Discount from '@/data/interfaces/Discount';
import cartApi from '@/services/cartApi';
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface CartContextType {
	cartItems: CartItem[];
	discount?: Discount;
	totals?: CartTotals;
	addToCart: (item: {id: number, quantity: number, image_url: string}) => Promise<void>;
	updateQuantity: (productId: number, newQuantity: number) => Promise<void>;
	removeFromCart: (id: number) => Promise<void>;
	cartId: string | null;
	clearCart: () => void;
	setCartItems: (items: CartItem[]) => void;
	setCartId: (id: string | null) => void;
	applyDiscountCode: (code: string) => Promise<string>;
	loadCartData: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
	const context = useContext(CartContext);
	if (!context) throw new Error("useCart must be used within CartProvider");
	return context;
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
	const [cartItems, setCartItems] = useState<CartItem[]>([]);

	const [discount, setDiscount] = useState<Discount>();
	const [totals, setTotals] = useState<CartTotals>();

	const [cartId, setCartId] = useState<string | null>(() => {
		return localStorage.getItem('cartId');
	});

	// Save cartId to localStorage whenever it changes
	useEffect(() => {
		if (cartId) {
			localStorage.setItem('cartId', cartId);
		} else {
			localStorage.removeItem('cartId');
		}
	}, [cartId]);

	// Create cart on initial load if none exists
	useEffect(() => {
		loadCartData();
  	}, []); // Run only once on mount

	const createCart = async () => {
		try {
			const newCartId = await cartApi.createNewCart();
			setCartId(newCartId);
			_resetCartItems(); 
			return newCartId;
		} catch (error) {
			console.error('Error creating new cart:', error);
		}
	};

	const _actionWithCartCreation = async (action: any) => {
		try {
			const currentCartId = cartId || await createCart();
			if (!currentCartId) throw new Error('Failed to create cart');

			return await action(currentCartId);
		}catch (error: any) {
			if(error instanceof CartNotFoundError){
				clearCart();
				return await action(await createCart());
			}
			throw error;
		}
	}

	const loadCartData = async () => {
		try {
			await _actionWithCartCreation(async (currentCartId: string) => {
				const cart = await cartApi.fetchCartData(currentCartId);
				_setCartData(cart);
			});
		} catch (error: any) {
			console.error('Error fetching cart:', error);
		}
	}

	const _resetCartItems = () => {
		if(discount) setDiscount(undefined);
		if(cartItems?.length > 0) setCartItems([]); // Reset cart items for new cart
	}

	const _setCartData = (cart: Cart) => {
		setDiscount(cart.discount);
		setTotals(cart.totals);
		setCartItems(cart.items);
	}

	const addToCart = async (item: {id: number, quantity: number, image_url: string}) => {
		try {
			await _actionWithCartCreation(async (currentCartId: string) => {
				const existingItem = cartItems.find(x => x.id === item.id);
				if(existingItem)
					return await updateQuantity(item.id, existingItem.quantity+item.quantity);
				
				const cart = await cartApi.addItem(currentCartId, item.id, item.quantity, item.image_url);
				_setCartData(cart);
			})
		} catch (error: any) {
			console.error('Error adding item to cart:', error);
		}
	};

	const removeFromCart = async (id: number) => {
		try {
			await _actionWithCartCreation(async (currentCartId: string) => {
				const item = cartItems.find(item => item.id === id);
				if (!item?.cart_item_id) return;

				_setCartData(await cartApi.removeItem(currentCartId, item.cart_item_id));
			})
		} catch (error: any) {
			console.error('Error removing item from cart:', error);
		}
	};

	const updateQuantity = async (productId: number, newQuantity: number) => {
		try {
			await _actionWithCartCreation(async (currentCartId: string) => {
				const cart = await cartApi.updateItem(currentCartId, productId, newQuantity)
				_setCartData(cart);
			});
		} catch (error) {
			console.error('Error updating quantity:', error);
		}
	};

	const clearCart = () => {
		localStorage.removeItem('cartId');
		setCartId(null);
		_resetCartItems();
	};

	const applyDiscountCode = async (code: string) => {
		return await _actionWithCartCreation(async (currentCartId: string) => {
			if(!code || code.trim().length === 0)
				throw new Error("Įveskite kodą");

			const {message, cart} = await cartApi.applyDiscountCode(currentCartId, code);
			_setCartData(cart);
			return message;
		});
	}

	return (
		<CartContext.Provider value={{
			cartItems,
			addToCart,
			updateQuantity,
			removeFromCart,
			cartId,
			clearCart,
			setCartItems,
			setCartId,
			applyDiscountCode,
			discount,
			totals,
			loadCartData
		}}>
			{children}
		</CartContext.Provider>
	);
};